import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from "react";
import { IconMusic, IconPlayerPause, IconPlayerPlay, } from "@tabler/icons-react";
import { ImageWithLoader } from "@/shared/ui/ImageWithLoader";
import styles from "./SongPreviewCard.module.scss";
/** Секунды → "м:сс". */
function fmt(sec) {
    if (!Number.isFinite(sec) || sec < 0)
        return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}
/**
 * Лёгкая карточка исходной песни: обложка + play/pause на общем `<audio>` +
 * название и опциональная подпись. В отличие от `ExtendPlayer` — без waveform
 * (точку выбирать не нужно): используется на страницах «Видеообложка» и
 * «Караоке-клип», где нужно лишь прослушать исходный трек.
 *
 * Проигрывание кросс-доменно работает без CORS, поэтому `crossOrigin` не ставим.
 * Нет `audioUrl` → деградируем в карточку без кнопки воспроизведения.
 */
export const SongPreviewCard = ({ title, audioUrl, imageUrl, metaLabel, language, }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [coverFailed, setCoverFailed] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        const onTime = () => setCurrentTime(audio.currentTime);
        const onMeta = () => {
            setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
        };
        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("ended", onEnded);
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
        };
    }, [audioUrl]);
    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio)
            return;
        if (audio.paused) {
            void audio.play().catch(() => { });
        }
        else {
            audio.pause();
        }
    };
    // Перемотка бегунком: правим позицию в `<audio>` и синхронно в состоянии.
    const seek = (e) => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const t = Number(e.target.value);
        audio.currentTime = t;
        setCurrentTime(t);
    };
    const showCover = Boolean(imageUrl) && !coverFailed;
    const canPlay = Boolean(audioUrl);
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const safeCurrent = Math.min(Math.max(0, currentTime), safeDuration || 0);
    // Прогресс для заливки трека перемотки (в процентах).
    const progress = safeDuration ? (safeCurrent / safeDuration) * 100 : 0;
    return (_jsxs("div", { className: styles.card, children: [audioUrl && (_jsx("audio", { ref: audioRef, src: audioUrl, preload: "metadata" })), _jsxs("button", { type: "button", className: styles.cover, onClick: togglePlay, disabled: !canPlay, "aria-label": isPlaying
                    ? language === "ru"
                        ? "Пауза"
                        : "Pause"
                    : language === "ru"
                        ? "Воспроизвести"
                        : "Play", children: [showCover ? (_jsx(ImageWithLoader, { className: styles.coverImg, loaderSize: "sm", src: imageUrl, alt: "", onError: () => setCoverFailed(true) })) : (_jsx("span", { className: styles.coverPlaceholder, children: _jsx(IconMusic, { size: 28, stroke: 2 }) })), canPlay && (_jsx("span", { className: styles.coverOverlay, children: isPlaying ? (_jsx(IconPlayerPause, { size: 28, color: "#fff" })) : (_jsx(IconPlayerPlay, { size: 28, color: "#fff" })) }))] }), _jsxs("div", { className: styles.info, children: [_jsx("div", { className: styles.title, children: title }), metaLabel && _jsx("div", { className: styles.meta, children: metaLabel }), canPlay && (_jsxs("div", { className: styles.progress, children: [_jsx("input", { type: "range", min: 0, max: safeDuration || 1, step: 0.1, value: safeCurrent, onChange: seek, className: styles.seek, style: { "--vp-seek-progress": `${progress}%` }, "aria-label": language === "ru" ? "Перемотка" : "Seek", disabled: !safeDuration }), _jsxs("div", { className: styles.timeRow, children: [_jsx("span", { className: styles.time, children: fmt(safeCurrent) }), _jsx("span", { className: styles.time, children: fmt(safeDuration) })] })] }))] })] }));
};
